import type { AppSettingName, AppSettings } from './interfaces';

import { TLD } from '@/domains/core/settings/constants';

import logger from '../observability/logger';

export const defaultSettings: AppSettings = {
  ALGOLIA_APP_DEFAULT_INDEX: '',
  ALGOLIA_APP_ID: '',
  ALGOLIA_APP_INDEXES: '',
  ALGOLIA_APP_KEY: '',
  ALGOLIA_APP_SUGGESTIONS_INDEXES: '',
  ALGOLIA_APP_TIMEOUT: '300',
  ALGOLIA_HEADER_SEARCH_PRODUCTS_TIMEOUT: '500',
  ALGOLIA_HEADER_SEARCH_SUGGESTIONS_TIMEOUT: '500',
  ALGOLIA_METIS_APP_DEFAULT_INDEX: '',
  ALGOLIA_METIS_APP_ID: '',
  ALGOLIA_METIS_APP_INDEXES: '',
  ALGOLIA_METIS_APP_KEY: '',
  ALGOLIA_METIS_APP_SUGGESTIONS_INDEXES: '',
  ALGOLIA_SUGGESTIONS_APP_ID: '',
  ALGOLIA_SUGGESTIONS_APP_KEY: '',
  ALGOLIA_SUGGESTIONS_APP_TIMEOUT: '150',
  ALGOLIA_USE_PROXY: 'false',
  B2B_BULK_PURCHASE_URL_CLIENT: '',
  CACHE_CONTROL_MAX_AGE: `${process.env.CACHE_CONTROL_MAX_AGE || ''}`,
  CACHE_CONTROL_MAX_AGE_OTHER_BOT: `${
    process.env.CACHE_CONTROL_MAX_AGE_OTHER_BOT || ''
  }`,
  CACHE_CONTROL_MAX_AGE_SEA_BOT: `${
    process.env.CACHE_CONTROL_MAX_AGE_SEA_BOT || ''
  }`,
  CACHE_CONTROL_MAX_AGE_SEO_BOT: `${
    process.env.CACHE_CONTROL_MAX_AGE_SEO_BOT || ''
  }`,
  CACHE_CONTROL_S_MAXAGE: `${process.env.CACHE_CONTROL_S_MAXAGE || ''}`,
  CACHE_CONTROL_STALE_WHILE_REVALIDATE: `${
    process.env.CACHE_CONTROL_STALE_WHILE_REVALIDATE || ''
  }`,
  COOKIES_TO_CLEAN: '',
  DD_VERSION: '2',
  DIDOMI_NOTICE_ID: '',
  ENVIRONMENT: '',
  EXTERNAL_TAG_GOOGLE_OPTIMIZE_ID: '',
  EXTERNAL_TAG_GTM_ID: '',
  EXTERNAL_TAG_GTM_ID_FOR_PROLANDING: '',
  EXTERNAL_TAG_GTM_ID_PROD: '',
  EXTERNAL_TAG_GTM_URL: '',
  EXTERNAL_TAG_IADVIZE_SID: '',
  EXTERNAL_TAG_IADVIZE_URL: '',
  GOOGLE_MAPS_API_KEY: '',
  GRAPHQL_GATEWAY_URL: '',
  HEADER_MENU_REQUEST_TIMEOUT: '500',
  INFRA_ALGOLIA_PROXY_HOST: '',
  INFRA_ALGOLIA_PROXY_PORT: '80',
  INFRA_ALGOLIA_PROXY_SCHEME: 'http',
  INFRA_GRAPHQL_GW_WEB_HOST: '',
  INFRA_GRAPHQL_GW_WEB_ORIGIN_HOST: '',
  INFRA_GRAPHQL_GW_WEB_PORT: '',
  INFRA_GRAPHQL_GW_WEB_SCHEME: '',
  INSPIRATION_PAGE_API_SERVICE_TIMEOUT: '3000',
  KLARNA_API_PUBLIC_KEY: `${process.env.KLARNA_API_PUBLIC_KEY}`,
  KLARNA_API_URL: `${process.env.KLARNA_API_URL || ''}`,
  LEVIA_API_KEY: `${process.env.LEVIA_API_KEY}`,
  MS_ABTEST_APPLI_URL: '',
  MS_API_URL: `${process.env.INFRA_KONG_API_SCHEME}://${process.env.INFRA_KONG_API_HOST}:${process.env.INFRA_KONG_API_PORT}`,
  MS_API_URL_CLIENT: `${
    process.env.BROWSERAPI_PROTOCOL
  }://${process.env.INFRA_KONG_BROWSERAPI_ADDR?.replace('.com', TLD)}`,
  MS_CATALOG_ATTRIBUTE_TIMEOUT: '200',
  MS_CATALOG_CATEGORY_TIMEOUT: '300',
  MS_LEGACY_APPLI_URL: '',
  MS_MERCHANDIZING_MANAGER_TIMEOUT: '200',
  MS_PAYMENT_API_KEY: '',
  MS_PRODUCT_LISTING_CATEGORY_HUB_TIMEOUT: '300',
  MS_PRODUCT_LISTING_CATEGORY_TIMEOUT: '300',
  MS_PRODUCT_LISTING_MASS_LANDING_TIMEOUT: '300',
  MS_PRODUCT_LISTING_PROMO_HUB_TIMEOUT: '200',
  MS_PRODUCT_LISTING_SEARCH_TIMEOUT: '300',
  MS_RECOMMENDATION_A2C_POPIN_TIMEOUT: '200',
  MS_SPONSORED_BRAND_PRODUCTS_PAGE_TIMEOUT: '2000',
  MS_SPONSORED_PRODUCT_PRODUCT_PAGE_IDS_TIMEOUT: '400',
  MS_SPONSORED_PRODUCT_PRODUCT_PAGE_TIMEOUT: '2000',
  MS_SPONSORED_PRODUCT_TIMEOUT: '200',
  MS_STANDALONE_SERVICES_REQUEST_TIMEOUT: '100',
  NOOP_URL_FOR_DEV_PURPOSE: '',
  ONEY_MERCHANT_GUID: `${process.env.ONEY_MERCHANT_GUID || ''}`,
  PAINT_CATEGORIES_IDS: '',
  PAINT_MODEL_IDS_NO_REDIRECT: '',
  PAYPAL_CLIENT_ID: '',
  PLATFORM_CONFIG_TIMEOUT: '500',
  PLATFORM_ENVIRONMENT: 'development',
  PRODUCT_PAGE_API_SERVICE_TIMEOUT: '3000',
  PRODUCT_PAGE_DELIVERY_TIMEOUT: '500',
  PROJECT_PAGE_API_SERVICE_TIMEOUT: '3000',
  RECOMMENDATION_CATEGORY_TIMEOUT: '700',
  RELEVANCE_RECO_SHOPBOT_SLUG_ID: '',
  RELEVANCE_RERANKING_SEARCH_SLUG_ID: '',
  REQUEST_TIMEOUT: '1500',
  RUNNING_COMMERCIAL_OPERATIONS_TIMEOUT: '500',
  RUNNING_FEATURE_FLAGS_TIMEOUT: '500',
  SEARCHBAR_EMPTYSEARCH_SUGGESTIONS: '{}',
  SHOP_THE_ROOM_API_SERVICE_TIMEOUT: '3000',
  SHOWROOM_API_SERVICE_TIMEOUT: '3000',
  STRIPE_API_PUBLIC_KEY: `${process.env.STRIPE_API_PUBLIC_KEY}`,
  TIPSHEET_API_SERVICE_TIMEOUT: '3000',
};

function getAppSettingsInstance() {
  if (typeof globalThis.__spartacux__ === 'undefined') {
    globalThis.__spartacux__ = {
      appSettings: {
        ...defaultSettings,
      },
    };
  } else if (typeof globalThis.__spartacux__.appSettings === 'undefined') {
    globalThis.__spartacux__.appSettings = {
      ...defaultSettings,
    };
  }

  return globalThis.__spartacux__.appSettings as AppSettings;
}

let isInitialized = false;
let isLoading = false;
let updatePromise: Promise<void> | undefined;

// Should only used once on app initialization.
export function setAppSettings(
  data: Partial<AppSettings>,
  options: { force?: boolean } = {}
) {
  const { force } = options;
  const entries = Object.entries(data);
  const hasEntries = entries.length > 0;

  if (hasEntries && (!isInitialized || force)) {
    (entries as [keyof AppSettings, string][]).forEach(([name, value]) => {
      getAppSettingsInstance()[name] = value;
    });

    isInitialized = true;
  }

  return getAppSettingsInstance();
}

const fetchSettings = async (shouldRetry = false): Promise<void> => {
  // We request the fetching in 2 cases:
  // 1. if the settings are not yet initialized and not fetching, we request the
  //    fetching.
  // 2. if the fetching request has failed and is being retried.
  if ((!isInitialized && !isLoading) || (isLoading && shouldRetry === false)) {
    isLoading = true;
    updatePromise = fetch('/api/v1/settings')
      .then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          setAppSettings(data, { force: true });
          isLoading = false;
          return;
        }

        throw Error(
          `Request failed with status: [${response.status}] ${response.statusText}`
        );
      })
      .catch((error) => {
        if (shouldRetry) {
          return fetchSettings(false);
        }

        isLoading = false;
        logger.error(
          Error(`Unable to retrieve static settings: ${error.message}`)
        );

        throw error;
      });
  }

  return updatePromise;
};

export async function getLazyAppSetting(
  name: AppSettingName
): Promise<AppSettings[AppSettingName] | string> {
  if (typeof window !== 'undefined' && !isInitialized) {
    await fetchSettings(true);
  }

  return getAppSetting(name);
}

// On server-side, the settings are in the process.env objects, so we consume
// them.
// On client-side, the settings are injected from the SSR data and read with the
// getAppSetting/getAppSettings methods.
export function getAppSetting(
  name: AppSettingName
): AppSettings[AppSettingName] | string {
  if (typeof window === 'undefined') {
    // As the env name can be suffixed with _SERVER and _CLIENT, we provide some
    // backup solutions.
    return (
      process.env[`${name}_SERVER`] ||
      process.env[name] ||
      getAppSettingsInstance()[name] ||
      defaultSettings[name]
    );
  }

  if (
    !isInitialized &&
    window.__NEXT_DATA__?.props?.pageProps?.appSettings !== undefined
  ) {
    setAppSettings(window.__NEXT_DATA__.props.pageProps.appSettings);
  }

  if (!isInitialized) {
    throw new Error(
      `App setting '${name}' was requested before app settings were populated.`
    );
  }

  return getAppSettingsInstance()[name] || defaultSettings[name];
}
